import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/card';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast({
        title: t('common.error'),
        description: t('forgotPassword.emailRequired'),
        variant: 'destructive'
      });
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      
      setSent(true);
      toast({
        title: t('forgotPassword.toastTitle'),
        description: t('forgotPassword.toastDescription')
      });
    } catch (error) {
      console.error('Forgot password error:', error);
      // Always show success message to prevent email enumeration
      setSent(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FAF8F5] via-white to-[#FAF8F5] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/login" className="text-gray-600 hover:text-gray-900 transition">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <CardTitle className="text-2xl font-bold">{t('forgotPassword.title')}</CardTitle>
          </div>
          <CardDescription>
            {sent 
              ? t('forgotPassword.descriptionSent')
              : t('forgotPassword.descriptionForm')}
          </CardDescription>
        </CardHeader>

        {sent ? (
          <CardContent className="space-y-6">
            <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
              <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
              <h3 className="font-semibold text-green-900 mb-2">{t('forgotPassword.sentHeading')}</h3>
              <p className="text-sm text-green-800 mb-4">
                {t('forgotPassword.sentBodyBefore')} <strong>{email}</strong>{t('forgotPassword.sentBodyAfter')}
              </p>
              <p className="text-xs text-green-700">
                {t('forgotPassword.sentHint')}
              </p>
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => setSent(false)}
                variant="outline"
                className="w-full"
              >
                {t('forgotPassword.tryDifferent')}
              </Button>
              <Link to="/login" className="block">
                <Button variant="ghost" className="w-full">
                  {t('forgotPassword.backToLogin')}
                </Button>
              </Link>
            </div>
          </CardContent>
        ) : (
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label htmlFor="email" className="text-sm font-medium text-gray-700">
                  {t('forgotPassword.emailLabel')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <Input
                    id="email"
                    type="email"
                    placeholder={t('forgotPassword.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10"
                    required
                    autoFocus
                  />
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-xs text-blue-800">
                  <strong>{t('forgotPassword.noteLabel')}</strong> {t('forgotPassword.noteText')}
                </p>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col space-y-3">
              <Button
                type="submit"
                className="w-full bg-[#be1f1a] hover:bg-[#a71b17]"
                disabled={loading}
              >
                {loading ? t('forgotPassword.sending') : t('forgotPassword.sendLink')}
              </Button>
              
              <Link to="/login" className="w-full">
                <Button type="button" variant="outline" className="w-full">
                  {t('common.cancel')}
                </Button>
              </Link>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
};

export default ForgotPasswordPage;
